import { useEffect, useRef, useState, type ReactNode } from 'react';

import { css, keyframes } from '@compiled/react';
import {
	getRichTextTagsFromTextFormat,
	isRichTextNodeType,
	isRichTextValueConsideredEmpty,
} from '@uniformdev/richtext';

import { wacToken } from '@/libraries/design/foundations';

import type { ParameterRichTextValue, RichTextNode } from '@uniformdev/richtext';

const terminalGlowInKeyframes = keyframes({
	'0%': { opacity: 0, filter: 'blur(8px)' },
	'100%': { opacity: 1, filter: 'blur(0)' },
});

const terminalGlowInCss = css({
	// eslint-disable-next-line local/ensure-wac-token-usage
	animation: `2s ease-out 1s both ${terminalGlowInKeyframes}`,
});

const outerCss = css({
	display: 'inline-block',
	boxSizing: 'border-box',
	// A non-scaling transform still establishes the stacking context the colored
	// segment/cursor puffs rely on (`zIndex: -2` bubbling up to here). The visual
	// 0.9 "shrink" that used to live here is now folded into the fit-to-width
	// scale cap (`FIT_SCALE_MAX`) applied by the ancestor scaler, so the box can
	// shrink further than 0.9 on narrow screens without double-transforming.
	transform: 'translateZ(0)',
	transformOrigin: 'top left',
	position: 'relative',
	// Never allow the terminal box itself to grow wider than its container.
	maxWidth: '100%',
});

// Block-level wrapper whose width is the space available to the terminal. The
// fit-to-width hook measures this element's `clientWidth` as the budget the
// scaled content must fit within, so it MUST reflect the real available width
// rather than shrink to its (nowrap) content. `width: 100%` makes it fill the
// containing block even inside shrink-to-fit parents (e.g. a centered grid/flex
// item, as on the hero), and `minWidth: 0` lets it shrink below content size in
// flex/grid tracks. `maxWidth: 100%` is a final guard against the box itself
// ever exceeding the container.
const fitBoxCss = css({
	display: 'block',
	width: '100%',
	minWidth: 0,
	maxWidth: '100%',
	// Clip only horizontally so the decorative colored/white "puffs" (which are
	// positioned −60%/−150% beyond the text to poke past the terminal's left and
	// right edges) can never extend past the component's own width and overflow
	// the page on narrow viewports. `overflow-x: clip` leaves the vertical top/
	// bottom puffs intact (unlike `hidden`, which would create a scroll container
	// and risk clipping them).
	overflowX: 'clip',
});

// Holds the measured (natural-size) content and collapses to the *scaled*
// dimensions so the inline-block terminal box above hugs the fitted content
// (a bare `transform: scale` would leave the pre-scale layout size reserved).
//
// Clip the horizontal axis only: the sizer is the absolutely-positioned
// scaler's containing block, so `overflow-x: clip` here is what actually
// contains the scaled `nowrap` content and the left/right glow puffs. The
// vertical axis stays `visible` so the decorative top/bottom glow — which is
// designed to poke past the terminal's edges — is NOT sheared off flat at the
// box edge. Overflow never affects layout, so the sizer still collapses to the
// scaled footprint.
const fitSizerCss = css({
	position: 'relative',
	overflowX: 'clip',
	overflowY: 'visible',
});

// The element the fit scale is applied to. Scaling from the top-left keeps the
// content anchored to the terminal's origin. It stays in normal flow until the
// first measurement completes (so SSR / first paint reserve the real size);
// `fitScalerMeasuredCss` then lifts it out of flow so the sizer can collapse to
// the scaled footprint.
const fitScalerCss = css({
	transformOrigin: 'top left',
});

const fitScalerMeasuredCss = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
});

const textStaticCss = css({
	lineHeight: 1,
	fontWeight: 500,
	// eslint-disable-next-line local/ensure-wac-token-usage
	letterSpacing: '-0.1em',
	whiteSpace: 'nowrap',
});

const rowCss = css({
	display: 'inline-flex',
	alignItems: 'center',
});

const gridContainerCss = css({
	display: 'grid',
	gridTemplateColumns: '1fr',
	gridTemplateRows: '1fr',
});

const cursorBaseCss = css({
	display: 'inline-block',
	// eslint-disable-next-line local/ensure-wac-token-usage
	marginInlineStart: '0.04em',
	borderRadius: 4,
	transition: 'none',
	flex: '0 0 auto',
});

const cursorWrapperCss = css({
	position: 'relative',
	display: 'inline-flex',
	alignItems: 'center',
	flex: '0 0 auto',
});

const bgLayerCss = css({
	position: 'absolute',
	inset: 0,
	zIndex: -1,
	pointerEvents: 'none',
	backgroundColor: wacToken('color.primary.black'),
});

const segmentPuffCss = css({
	position: 'absolute',
	insetInlineStart: '-60%',
	insetInlineEnd: '-60%',
	// eslint-disable-next-line local/ensure-wac-token-usage
	filter: 'blur(10px)',
	pointerEvents: 'none',
	zIndex: -2,
});

const coloredSegmentWrapperCss = css({
	position: 'relative',
	display: 'inline-block',
});

// Both grid cells (ghost + visible) stack in the same single grid track.
const gridCellCss = css({
	gridColumn: 1,
	gridRow: 1,
});

// The ghost cell only reserves space — it is never shown.
const ghostCellCss = css({
	visibility: 'hidden',
});

const cursorPuffCss = css({
	position: 'absolute',
	insetInlineStart: '-150%',
	insetInlineEnd: '-150%',
	// eslint-disable-next-line local/ensure-wac-token-usage
	filter: 'blur(10px)',
	pointerEvents: 'none',
	zIndex: -2,
	transition: 'none',
});

export interface TerminalTypingSettings {
	typeMs: number;
	backspaceMs: number;
	holdMs: number;
	pauseMs: number;
	blinkMs: number;
	fontSizePt: number;
	/** Explicit box width in px, or null to use the ghost-measured natural width. */
	boxWidthPx: number | null;
	paddingPx: number;
	borderRadiusPx: number;
}

export const TERMINAL_TYPING_DEFAULTS: TerminalTypingSettings = {
	typeMs: 60,
	backspaceMs: 35,
	holdMs: 1500,
	pauseMs: 400,
	blinkMs: 500,
	fontSizePt: 36,
	boxWidthPx: null,
	paddingPx: 16,
	borderRadiusPx: 12,
};

interface Segment {
	text: string;
	color: string;
}

const PROMPT_COLOR = '#B1E4F7';
const WHITE = wacToken('color.neutral.light0');

/**
 * The set of highlight colors an editor may pick for a phrase's emphasized
 * (italic) word.
 */
export const HIGHLIGHT_COLORS = {
	lime: wacToken('color.extended.lime400'),
	// These three colors have no design-token equivalent;
	/* eslint-disable local/ensure-wac-token-usage */
	cyan: '#7BD3EA',
	saffron: wacToken('color.extended.saffron300'),
	coral: '#F87B6B',
	blue: '#1E88FF',
	/* eslint-enable local/ensure-wac-token-usage */
} as const;

export type HighlightColor = (typeof HIGHLIGHT_COLORS)[keyof typeof HIGHLIGHT_COLORS];

const DEFAULT_HIGHLIGHT_COLOR: HighlightColor = HIGHLIGHT_COLORS.lime;

/**
 * A single phrase as supplied by Uniform: the translated rich-text value
 * (where the editor italicizes the word to highlight) plus the highlight
 * color to paint that italic word.
 */
export interface PhraseInput {
	/** Uniform rich-text value. The italic (`<em>`) run is the highlighted word. */
	richText?: ParameterRichTextValue | { root: RichTextNode };
	/** One of {@link HIGHLIGHT_COLORS}. Falls back to {@link DEFAULT_HIGHLIGHT_COLOR}. */
	highlightColor?: string;
}

/** Extract the `{ root }` node from either Uniform value shape. */
function getRichTextRoot(value: PhraseInput['richText']): RichTextNode | undefined {
	if (!value || typeof value !== 'object') return undefined;
	const root = (value as { root?: unknown }).root;
	return root && typeof root === 'object' ? (root as RichTextNode) : undefined;
}

/**
 * Walk a Uniform rich-text tree and collapse it into the terminal's
 * `[white, highlighted, white, ...]` segment shape. Any text node whose
 * format includes italic (`<em>`) becomes a highlighted segment painted in
 * `highlightColor`; everything else is white. Consecutive runs of the same
 * color are merged so the typing animation advances one visual word at a time.
 * The editor may place the italic word anywhere in the phrase.
 */
function richTextToSegments(root: RichTextNode, highlightColor: string): Segment[] {
	const segments: Segment[] = [];
	const pushText = (text: string, isItalic: boolean) => {
		if (!text) return;
		const color = isItalic ? highlightColor : WHITE;
		const last = segments[segments.length - 1];
		if (last && last.color === color) {
			last.text += text;
		} else {
			segments.push({ text, color });
		}
	};
	const walk = (node: RichTextNode) => {
		if (isRichTextNodeType(node, 'text')) {
			const isItalic = getRichTextTagsFromTextFormat(node.format).includes('em');
			pushText(node.text, isItalic);
			return;
		}
		node.children?.forEach(walk);
	};
	walk(root);
	return segments;
}

/**
 * Convert the editor-supplied phrases into the `Segment[][]` the typing
 * animation consumes. Empty phrases (no rich text, or rich text considered
 * empty by Uniform) are skipped so unused slots behave as if absent. Each
 * phrase is prefixed with the shared `>` prompt segment.
 */
function buildPhrases(phraseInputs: PhraseInput[]): Segment[][] {
	const promptSegment: Segment = { text: '>', color: PROMPT_COLOR };
	const phrases: Segment[][] = [];
	for (const input of phraseInputs) {
		const root = getRichTextRoot(input.richText);
		if (!root || isRichTextValueConsideredEmpty(input.richText as ParameterRichTextValue)) {
			continue;
		}
		const highlightColor = input.highlightColor || DEFAULT_HIGHLIGHT_COLOR;
		const segments = richTextToSegments(root, highlightColor);
		if (segments.length === 0) continue;
		phrases.push([promptSegment, ...segments]);
	}
	return phrases;
}

type Breakpoint = 'xsmall' | 'mobile' | 'tablet' | 'desktop';

interface SizeSpec {
	fontSize: string;
	cursorWidth: number;
	cursorHeight: number;
	padding: number;
	promptGap: number;
	borderRadius: number;
}

// Tablet/mobile sizing keeps the hero terminal readable on small screens.
// On desktop we fall back to the live `TerminalTypingSettings` (which the
// floating Typing controls panel can tune) — see the merge of `size` and
// `settings` below.
//
// These are the terminal's *natural* (unscaled) sizes per breakpoint. The
// terminal text is `white-space: nowrap` and never wraps, so on very narrow
// viewports the longest phrase can still be wider than the screen even at the
// smallest breakpoint's font size — which overflowed the page horizontally in
// every browser at ≤ 320px (no browser breaks a nowrap line). The component
// therefore *measures* the natural content width and the available width and
// applies a `transform: scale(fit)` so the terminal always shrinks to fit (see
// `useFitToWidth`). These per-breakpoint sizes stay as the readable targets for
// screens wide enough to show them at 1×.
const SIZE_BY_BREAKPOINT: Record<Breakpoint, SizeSpec> = {
	desktop: {
		fontSize: '40px',
		cursorWidth: 39,
		cursorHeight: 74,
		padding: 14,
		promptGap: 16,
		borderRadius: 12,
	},
	tablet: {
		fontSize: '36px',
		cursorWidth: 21,
		cursorHeight: 39,
		padding: 12,
		promptGap: 12,
		borderRadius: 10,
	},
	// Small screens (≥ 480px, < 768px).
	mobile: {
		fontSize: '32px',
		cursorWidth: 15,
		cursorHeight: 30,
		padding: 10,
		promptGap: 10,
		borderRadius: 8,
	},
	// X-small screens (< 480px). This is the band that overflowed at ≤ 320px;
	// the fit-to-width scaling (see `useFitToWidth`) guarantees it fits there.
	xsmall: {
		fontSize: '26px',
		cursorWidth: 12,
		cursorHeight: 24,
		padding: 8,
		promptGap: 8,
		borderRadius: 6,
	},
};

function getBreakpoint(width: number): Breakpoint {
	if (width >= 1024) return 'desktop';
	if (width >= 768) return 'tablet';
	if (width >= 480) return 'mobile';
	return 'xsmall';
}

function useBreakpoint(): Breakpoint {
	const [bp, setBp] = useState<Breakpoint>(() => {
		if (typeof window === 'undefined') return 'desktop';
		return getBreakpoint(window.innerWidth);
	});
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const onResize = () => setBp(getBreakpoint(window.innerWidth));
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);
	return bp;
}

/**
 * Measure the terminal's natural (unscaled) content width against the width
 * actually available to it, and return a `scale` ≤ 1 that makes the content fit.
 *
 * The terminal text is `white-space: nowrap`, so a long phrase has a fixed
 * intrinsic width that no browser will wrap. On narrow viewports (notably
 * ≤ 320px) that intrinsic width exceeds the screen and the page overflows
 * horizontally in every browser. Rather than approximate a fit with vw/clamp
 * font sizes (which can't guarantee a long phrase fits), we measure both widths
 * and scale the content down by exactly the ratio needed — deterministic and
 * identical across browsers.
 *
 * `contentRef` should point at the element whose natural width we want to keep
 * on screen (the ghost row, which always reserves the longest phrase + cursor).
 * `availableRef` should point at the element whose content-box width is the
 * space we must fit into (the terminal's padding box). `deps` re-measures when
 * sizing inputs (breakpoint, font size, phrases) change.
 */
interface FitToWidth {
	/** Multiplier ≤ `FIT_SCALE_MAX` to apply via `transform: scale(...)`. */
	scale: number;
	/** Natural (unscaled) content width in px, or null before first measure. */
	naturalWidth: number | null;
	/** Natural (unscaled) content height in px, or null before first measure. */
	naturalHeight: number | null;
}

// The terminal's baseline (un-shrunk) scale. The hero historically renders at
// 0.9×; fit-to-width only ever scales *down* from here on narrow viewports.
const FIT_SCALE_MAX = 0.9;

function useFitToWidth(
	contentRef: React.RefObject<HTMLElement | null>,
	availableRef: React.RefObject<HTMLElement | null>,
	deps: ReadonlyArray<unknown>,
): FitToWidth {
	const [fit, setFit] = useState<FitToWidth>({
		scale: FIT_SCALE_MAX,
		naturalWidth: null,
		naturalHeight: null,
	});

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const content = contentRef.current;
		const available = availableRef.current;
		if (!content || !available) return;

		const measure = () => {
			// Natural, unscaled size of the longest phrase + cursor.
			const naturalWidth = content.scrollWidth;
			const naturalHeight = content.scrollHeight;
			// Width available to the terminal. Prefer the *containing block's*
			// content width (`available.parentElement`), because a shrink-to-fit
			// parent (e.g. a centered grid/flex item, as on the hero) can make the
			// wrapper itself size to its nowrap content and report a width that is
			// already too wide — which would make the fit ratio ~1 and skip the
			// shrink. The parent's content box is the true budget. Fall back to the
			// wrapper's own `clientWidth` when there is no parent.
			const parent = available.parentElement;
			const availableWidth = parent ? parent.clientWidth : available.clientWidth;
			if (naturalWidth <= 0 || availableWidth <= 0) return;
			// Cap at the baseline (never enlarge past it); shrink further when the
			// natural content is wider than the available space.
			const next = Math.min(FIT_SCALE_MAX, availableWidth / naturalWidth);
			setFit((prev) =>
				// Avoid thrashing on sub-pixel jitter.
				Math.abs(prev.scale - next) > 0.005 ||
				prev.naturalWidth !== naturalWidth ||
				prev.naturalHeight !== naturalHeight
					? { scale: next, naturalWidth, naturalHeight }
					: prev,
			);
		};

		// Measure synchronously once (deterministic first paint), then again on the
		// next frame (to catch fonts/async layout), and on every resize.
		measure();
		const raf = window.requestAnimationFrame(measure);
		const ro =
			typeof ResizeObserver !== 'undefined'
				? new ResizeObserver(() => window.requestAnimationFrame(measure))
				: undefined;
		ro?.observe(available);
		ro?.observe(content);
		window.addEventListener('resize', measure);
		return () => {
			window.cancelAnimationFrame(raf);
			ro?.disconnect();
			window.removeEventListener('resize', measure);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);

	return fit;
}

function phraseLength(segments: Segment[]) {
	return segments.reduce((n, s) => n + s.text.length, 0);
}

function findLongestIndex(phrases: Segment[][]): number {
	let idx = 0;
	let max = -1;
	phrases.forEach((p, i) => {
		const len = phraseLength(p);
		if (len > max) {
			max = len;
			idx = i;
		}
	});
	return idx;
}

// Number of characters in the prompt segment (`>`). Backspacing stops here
// so the prompt stays visible across phrase swaps and the cursor parks
// at the position of the first letter (after the prompt gap).
const PROMPT_LENGTH = 1;

function renderSegments(
	segments: Segment[],
	charLimit: number,
	promptGap: number,
	paddingPx: number,
	visible: boolean,
): ReactNode[] {
	const nodes: ReactNode[] = [];
	let remaining = charLimit;
	for (let i = 0; i < segments.length; i++) {
		const seg = segments[i] as Segment;
		// Add a viewport-scaled gap immediately after the prompt segment ('>')
		const isPrompt = i === 0;
		const style: React.CSSProperties = { color: seg.color };
		if (isPrompt) style.marginRight = promptGap;
		if (remaining <= 0) {
			nodes.push(
				<span key={i} style={style}>
					{''}
				</span>,
			);
			continue;
		}
		const slice = seg.text.slice(0, remaining);
		const isColoured = seg.color !== WHITE;
		// For coloured segments we wrap the letters in a relative inline-block
		// and drop two blurred "puffs" of the segment's colour that sit just
		// outside the terminal — above and below — so they tint only the
		// slice of the surrounding blue halo that lines up with the coloured
		// word. The rest of the halo stays its original blue.
		// The hidden ghost render (used to lock the terminal's width) must
		// NOT paint puffs, otherwise they would show up at the longest
		// phrase's positions even before that phrase types in.
		if (isColoured && visible) {
			const c = seg.color;
			// Tall puff that extends from outside the terminal edge into the
			// terminal interior. The colored span's wrapper has no stacking
			// context, so the puff's `zIndex: -2` bubbles up to the outer
			// terminal stacking context where the opaque background layer
			// (`zIndex: -1`) covers the inside portion — leaving only the
			// sliver that pokes past the terminal edge visible against the
			// surrounding blue halo.
			const sliver = 6;
			const puffHeight = Math.max(36, paddingPx * 2.6);
			const edgeOffset = -(paddingPx + sliver);
			// Runtime-only puff values (the rest live in segmentPuffCss).
			const sharedPuff: React.CSSProperties = {
				height: puffHeight,
				background: `radial-gradient(50% 100% at 50% 50%, ${c}E6 0%, ${c}80 35%, ${c}33 65%, transparent 85%)`,
			};
			nodes.push(
				<span key={i} css={coloredSegmentWrapperCss} style={style}>
					<span aria-hidden css={segmentPuffCss} style={{ ...sharedPuff, top: edgeOffset }} />
					<span aria-hidden css={segmentPuffCss} style={{ ...sharedPuff, bottom: edgeOffset }} />
					{slice}
				</span>,
			);
		} else {
			nodes.push(
				<span key={i} style={style}>
					{slice}
				</span>,
			);
		}
		remaining -= slice.length;
	}
	return nodes;
}

function usePrefersReducedMotion() {
	const [reduced, setReduced] = useState<boolean>(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return false;
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	});
	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return;
		const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
		const handler = () => setReduced(mq.matches);
		mq.addEventListener?.('change', handler);
		return () => mq.removeEventListener?.('change', handler);
	}, []);
	return reduced;
}

type Phase = 'typing' | 'backspacing';

const CURSOR_PUFF_GRADIENT = `radial-gradient(50% 100% at 50% 50%, #FFFFFFCC 0%, #FFFFFF66 35%, #FFFFFF22 65%, transparent 85%)`;

interface TerminalCursorProps {
	cursorStyle: React.CSSProperties;
	padding: number;
	showCursor: boolean;
}

// The blinking cursor plus its top/bottom white halo "puffs" that poke past
// the terminal edge. Extracted to keep the main component's complexity down.
const TerminalCursor = ({ cursorStyle, padding, showCursor }: TerminalCursorProps) => {
	const sliver = 6;
	const puffHeight = Math.max(36, padding * 2.6);
	const edgeOffset = -(padding + sliver);
	const puffStyle: React.CSSProperties = {
		height: puffHeight,
		background: CURSOR_PUFF_GRADIENT,
		opacity: showCursor ? 0.5 : 0,
	};
	return (
		<span css={cursorWrapperCss}>
			<span aria-hidden css={cursorPuffCss} style={{ ...puffStyle, top: edgeOffset }} />
			<span aria-hidden css={cursorPuffCss} style={{ ...puffStyle, bottom: edgeOffset }} />
			<span css={cursorBaseCss} style={cursorStyle} />
		</span>
	);
};

interface ResolvedSizing {
	cursorWidth: number;
	cursorHeight: number;
	fontSize: string;
	padding: number;
	borderRadius: number;
	promptGap: number;
}

/**
 * Resolve the terminal's pixel sizing for the current breakpoint. On desktop the
 * appearance is driven by `settings` (so the Typing controls panel can tune it
 * live); on tablet/mobile it falls back to the breakpoint-tuned `size` so the
 * hero terminal stays readable on small screens. Cursor dimensions scale with
 * font size on desktop (height = 1.6×, width = 0.84×) to keep proportion across
 * the full size range. Extracted from the component to keep its complexity down.
 */
function resolveSizing(settings: TerminalTypingSettings, breakpoint: Breakpoint): ResolvedSizing {
	const size = SIZE_BY_BREAKPOINT[breakpoint];
	if (breakpoint !== 'desktop') {
		return {
			cursorWidth: size.cursorWidth,
			cursorHeight: size.cursorHeight,
			fontSize: size.fontSize,
			padding: size.padding,
			borderRadius: size.borderRadius,
			promptGap: size.promptGap,
		};
	}
	return {
		cursorWidth: settings.fontSizePt * 0.84,
		cursorHeight: settings.fontSizePt * 1.6,
		fontSize: `${settings.fontSizePt}pt`,
		padding: settings.paddingPx,
		borderRadius: settings.borderRadiusPx,
		// Prompt gap is responsive across all breakpoints (not tunable).
		promptGap: size.promptGap,
	};
}

/** Number of fixed phrase slots exposed to Uniform editors. */
export const MAX_PHRASES = 8;

export interface TerminalTypingProps {
	settings?: TerminalTypingSettings;
	/**
	 * When true, the typing animation finishes the current phrase (letting any
	 * in-progress type/backspace complete) and then holds — it will not advance
	 * to the next phrase until `paused` becomes false again.
	 */
	paused?: boolean;
	/** Static heading rendered above the terminal. Translated by Uniform. */
	topLine?: string;
	/**
	 * Up to {@link MAX_PHRASES} phrases. Each is a Uniform rich-text value where
	 * the italic run is the highlighted word. Empty phrases are skipped, so
	 * unused slots behave as if they were never supplied.
	 */
	phrases?: PhraseInput[];
}

export const UniformTerminalTyping = ({
	settings = TERMINAL_TYPING_DEFAULTS,
	paused = false,
	topLine,
	phrases: phraseInputs,
}: TerminalTypingProps) => {
	const reducedMotion = usePrefersReducedMotion();
	const breakpoint = useBreakpoint();
	const isDesktop = breakpoint === 'desktop';
	const phrases = buildPhrases(phraseInputs ?? []);
	const longestIndex = findLongestIndex(phrases);
	const [phraseIndex, setPhraseIndex] = useState(0);

	// Fit-to-width: measure the whole terminal box (`fitScalerRef`) against the
	// space available to the component (`fitBoxRef`) and shrink it to fit. This
	// is what keeps the `white-space: nowrap` terminal from overflowing the page
	// horizontally on very narrow viewports (≤ 320px) in every browser — a bare
	// font-size step can't guarantee a long phrase fits, but scaling by the exact
	// measured ratio always does. On wide-enough screens `scale` stays at the
	// `FIT_SCALE_MAX` baseline (0.9) — i.e. the historical appearance is unchanged.
	//
	// These hooks (refs + the measuring hook) must run unconditionally, before
	// the empty-phrases early return below, to satisfy the rules of hooks. Their
	// re-measure signal is derived from the longest phrase; when there are no
	// phrases it is a stable empty string, which is harmless.
	const fitBoxRef = useRef<HTMLDivElement | null>(null);
	const fitScalerRef = useRef<HTMLDivElement | null>(null);
	const longestSignal = (phrases[longestIndex] ?? [])
		.map((s) => s.text)
		.join('\u0000');
	const { scale, naturalWidth, naturalHeight } = useFitToWidth(fitScalerRef, fitBoxRef, [
		breakpoint,
		// Re-measure when sizing-affecting inputs change. Font size and padding
		// derive deterministically from the breakpoint + settings, so keying on
		// those plus the breakpoint and the longest-phrase text covers all cases.
		settings.fontSizePt,
		settings.paddingPx,
		longestSignal,
	]);
	// Start at PROMPT_LENGTH so the leading `>` is visible from first paint
	// and never gets typed/erased during the loop.
	const [charCount, setCharCount] = useState(PROMPT_LENGTH);
	const [phase, setPhase] = useState<Phase>('typing');
	const [cursorVisible, setCursorVisible] = useState(true);

	// `phrases` can be empty if every slot was left blank. Default to an empty
	// segment list so the typing effects below never read `undefined`; the
	// component early-returns `null` (after all hooks) when there are none.
	const currentSegments = (phrases[phraseIndex] ?? []) as Segment[];
	const currentTotal = phraseLength(currentSegments);
	// Per spec: cursor blinks only after a phrase is fully typed, until
	// backspacing begins. While typing, backspacing, or in the pause
	// between phrases, the cursor stays solid.
	const idle = phase === 'typing' && charCount >= currentTotal;

	// Drive typing / backspacing
	useEffect(() => {
		if (reducedMotion) return;
		let id: ReturnType<typeof setTimeout> | undefined;
		if (phase === 'typing') {
			if (charCount < currentTotal) {
				// Always let an in-progress phrase finish typing — even while paused —
				// so the visible word lands on a complete state.
				id = setTimeout(() => setCharCount((c) => c + 1), settings.typeMs);
			} else if (!paused) {
				// Fully typed: hold, then start backspacing. While paused, hold here
				// indefinitely (no schedule) until `paused` flips back to false.
				id = setTimeout(() => setPhase('backspacing'), settings.holdMs);
			}
		} else {
			if (charCount > PROMPT_LENGTH) {
				// Backspacing is also part of "the current message finishing", so we
				// let it complete even while paused.
				id = setTimeout(() => setCharCount((c) => c - 1), settings.backspaceMs);
			} else {
				// Erased back to the prompt: always advance to the next phrase, even
				// while paused. This way, if pause arrived mid-backspace, we still
				// settle on a fully-typed phrase (the next one) rather than holding
				// on the bare prompt. The hold itself happens in the `phase==='typing'
				// && fully typed` branch above.
				id = setTimeout(() => {
					setPhraseIndex((i) => (i + 1) % phrases.length);
					setPhase('typing');
				}, settings.pauseMs);
			}
		}
		return () => {
			if (id) clearTimeout(id);
		};
	}, [
		phase,
		charCount,
		currentTotal,
		reducedMotion,
		paused,
		settings.typeMs,
		settings.backspaceMs,
		settings.holdMs,
		settings.pauseMs,
	]);

	// Cursor blink — only while idle (fully typed or fully erased)
	useEffect(() => {
		if (reducedMotion) {
			setCursorVisible(true);
			return;
		}
		if (!idle) {
			setCursorVisible(true);
			return;
		}
		const id = setInterval(() => setCursorVisible((v) => !v), settings.blinkMs);
		return () => clearInterval(id);
	}, [idle, reducedMotion, settings.blinkMs]);

	// Nothing to type if every phrase slot was left empty. All hooks above have
	// already run, so this early return keeps the rules of hooks intact.
	if (phrases.length === 0) {
		return null;
	}

	// Reduced motion: render static phrase 1 with non-blinking cursor
	const displaySegments = (reducedMotion ? phrases[0] : currentSegments) as Segment[];
	const displayCount = reducedMotion ? phraseLength(phrases[0] as Segment[]) : charCount;
	const showCursor = reducedMotion ? true : cursorVisible;

	const longestSegments = phrases[longestIndex] as Segment[];
	const longestCount = phraseLength(longestSegments);

	// Resolve all responsive pixel sizing for this breakpoint (see
	// `resolveSizing` for the desktop-vs-small-screen behavior).
	const { cursorWidth, cursorHeight, fontSize, padding, borderRadius, promptGap } = resolveSizing(
		settings,
		breakpoint,
	);

	// Cursor fills the terminal's content area. The cursor's explicit
	// pixel height drives the terminal's interior height; combined with
	// the box's padding, this leaves padding-px of dark space above and
	// below the cursor. The text is centered alongside it via the
	// inline-flex row.
	// Runtime-only cursor values (static ones live in cursorBaseCss).
	const cursorStyle: React.CSSProperties = {
		width: cursorWidth,
		height: cursorHeight,
		backgroundColor: WHITE,
		opacity: showCursor ? 1 : 0,
	};

	// Runtime-only text values (static ones live in textStaticCss).
	const textStyle: React.CSSProperties = {
		fontFamily: "'Atlassian Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
		fontSize,
		color: WHITE,
	};

	// Explicit box width is desktop-only — on tablet/mobile we always let
	// the ghost measure the natural width so the box scales naturally with
	// the responsive font size.
	const explicitWidth = isDesktop && typeof settings.boxWidthPx === 'number';
	// Runtime-only outer values (static ones live in outerCss). The transform
	// in outerCss creates the stacking context the colored puffs land in.
	const outerStyle: React.CSSProperties = {
		borderRadius,
		padding,
	};
	if (explicitWidth) {
		outerStyle.minWidth = settings.boxWidthPx as number;
	}

	// Derived fit values (not hooks — safe after the early return). The hooks
	// that produce `scale`/`naturalWidth`/`naturalHeight` run unconditionally
	// near the top of the component.
	const measured = naturalWidth != null && naturalHeight != null;
	// Collapse the sizer to the scaled footprint so the surrounding layout
	// reserves only the fitted size (transforms alone don't affect layout size).
	const sizerStyle: React.CSSProperties = measured
		? { width: naturalWidth * scale, height: naturalHeight * scale }
		: {};
	const scalerStyle: React.CSSProperties = { transform: `scale(${scale})` };

	return (
		<div ref={fitBoxRef} css={fitBoxCss}>
			<div css={fitSizerCss} style={sizerStyle}>
				<div
					ref={fitScalerRef}
					css={[fitScalerCss, measured && fitScalerMeasuredCss]}
					style={scalerStyle}
				>
					<div css={[terminalGlowInCss, outerCss]} style={outerStyle}>
						{/* Opaque terminal background layer. Sits above the colored puffs
                            (zIndex: -2) and below the text (which is in normal flow), so
                            it masks the inside-terminal portion of every puff. */}
						<span aria-hidden css={bgLayerCss} style={{ borderRadius }} />
						{topLine ? (
							<div css={textStaticCss} style={{ ...textStyle, marginBottom: promptGap }}>
								{topLine}
							</div>
						) : null}
						{/* aria-hidden because constantly-changing typed/backspaced text
                            would otherwise spam assistive tech. The static `topLine` heading
                            above stays exposed to screen readers. */}
						<div css={gridContainerCss} aria-hidden>
							{/* Invisible ghost — reserves width/height of the longest fully-typed phrase + cursor.
                                When an explicit box width is set, the outer container's overflow:hidden clips
                                any horizontal overflow, but the ghost still locks the height. */}
							<div css={[textStaticCss, rowCss, gridCellCss, ghostCellCss]} style={textStyle}>
								<span>
									{renderSegments(longestSegments, longestCount, promptGap, padding, false)}
								</span>
								<span css={cursorBaseCss} style={{ ...cursorStyle, opacity: 1 }} />
							</div>
							{/* Visible content */}
							<div css={[textStaticCss, rowCss, gridCellCss]} style={textStyle}>
								<span>{renderSegments(displaySegments, displayCount, promptGap, padding, true)}</span>
								{/* Cursor with matching top/bottom white halo sliver. Same
                                    behind-the-bg trick as the coloured segment puffs. */}
								<TerminalCursor cursorStyle={cursorStyle} padding={padding} showCursor={showCursor} />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

/**
 * Flat parameters as supplied by Uniform. Each phrase slot is a `richText`
 * value plus a `select` color, keyed `phraseN` / `phraseNColor`. These mirror
 * the parameter ids in `terminalTyping.json`. Slots are independent — an editor
 * can fill any subset; empty slots are skipped.
 */
export interface UniformTerminalTypingProps {
	topLine?: string;
	phrase1?: PhraseInput['richText'];
	phrase1Color?: string;
	phrase2?: PhraseInput['richText'];
	phrase2Color?: string;
	phrase3?: PhraseInput['richText'];
	phrase3Color?: string;
	phrase4?: PhraseInput['richText'];
	phrase4Color?: string;
	phrase5?: PhraseInput['richText'];
	phrase5Color?: string;
	phrase6?: PhraseInput['richText'];
	phrase6Color?: string;
	phrase7?: PhraseInput['richText'];
	phrase7Color?: string;
	phrase8?: PhraseInput['richText'];
	phrase8Color?: string;
}

/**
 * Uniform entry point. Collapses the flat `phraseN` / `phraseNColor` params
 * into the ordered `PhraseInput[]` the animation consumes, then renders the
 * core {@link UniformTerminalTyping} component.
 */
const UniformTerminalTypingComponent = (props: UniformTerminalTypingProps) => {
	const phrases: PhraseInput[] = [];
	for (let i = 1; i <= MAX_PHRASES; i++) {
		const richText = props[`phrase${i}` as keyof UniformTerminalTypingProps];
		const highlightColor = props[`phrase${i}Color` as keyof UniformTerminalTypingProps] as
			| string
			| undefined;
		phrases.push({
			richText: richText as PhraseInput['richText'],
			highlightColor,
		});
	}
	return <UniformTerminalTyping topLine={props.topLine} phrases={phrases} />;
};

export default UniformTerminalTypingComponent;
